<script setup lang="ts">
import SvgLoader from '@/utils/SvgLoader.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { computed, ref, StyleValue } from 'vue';
import { getRandomStr } from '@/businessRepository/idUtils';
import { i18n } from '@/i18n';

/** Emits */
const emits = defineEmits<{
  (e: 'onClickTransactionList'): void;
  (e: 'onSelectMenu', option: 'download' | 'forecastRequestMail' | 'edit'): void;
}>();

/** Variables */
const t = i18n.t;
const containerId = getRandomStr(16);
const isMenuOpenedState = ref<boolean>(false);
const menuButtonPosition = ref({
  x: 0,
  y: 0,
});

/** Menu items */
const MENU_ITEMS = [
  {
    value: 'download' as const,
    icon: 'downloadSmall',
    text: 'ダウンロード',
  },
  /*{
    value: 'forecastRequestMail' as const,
    icon: 'mailIcon',
    text: '内示依頼メール',
  },*/
  {
    value: 'edit' as const,
    icon: 'editPen',
    text: '編集',
  },
];

/** Function */
const onClickButton = () => {
  if (!isMenuOpenedState.value) {
    const buttonContent = document.getElementById(containerId);
    const bodyContent = document.getElementsByTagName('body')[0];
    if (!(buttonContent instanceof HTMLElement) || !(bodyContent instanceof HTMLElement)) return;
    menuButtonPosition.value = {
      x: buttonContent.offsetWidth,
      y: buttonContent.getBoundingClientRect().top,
    };
  }
  isMenuOpenedState.value = !isMenuOpenedState.value;
};

const dropdownMenuStyle = computed((): StyleValue => {
  return {
    top: `${menuButtonPosition.value.y + 20}px`,
    right: `${menuButtonPosition.value.x}px`,
  };
});

/**
 * メニューの選択肢を選んだ際の処理
 * @param value - 選択した値
 */
const selectOption = (value: (typeof MENU_ITEMS)[number]['value']) => {
  isMenuOpenedState.value = false;
  emits('onSelectMenu', value);
};
</script>

<template>
  <div class="row-menu">
    <TextButton class="row-menu__transaction-button" @click="emits('onClickTransactionList')">
      <p class="row-menu__transaction-button-text">{{ t('取引一覧') }}</p>
    </TextButton>
    <div :id="containerId" class="row-menu__ellipsis-container">
      <button class="row-menu__ellipsis-button" :class="{ 'row-menu__ellipsis-button--active': isMenuOpenedState }" @click="onClickButton">
        <SvgLoader class="row-menu__ellipsis-icon" name="menuEllipsis" />
      </button>
    </div>
  </div>
  <Teleport v-if="isMenuOpenedState" to="body">
    <div class="row-menu__bg" @click="onClickButton" />
    <div class="row-menu__dropdown" :style="dropdownMenuStyle">
      <div v-for="option in MENU_ITEMS" :key="option.value" class="row-menu__dropdown-content">
        <button class="row-menu__dropdown-option row-menu__dropdown-option--active" @click="selectOption(option.value)">
          <SvgLoader class="row-menu__dropdown-icon" :name="option.icon" />
          <div class="row-menu__dropdown-option-text">{{ t(option.text) }}</div>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.row-menu {
  display: grid;
  grid-template-columns: auto auto;
  gap: 4px;
  align-items: center;
  justify-content: center;
  height: 100%;

  &__transaction-button {
    min-width: 80px;
    min-height: 18px;
    height: fit-content;
    border-radius: 4px;
    display: grid;
    place-items: center;
    background-color: $semanticWhite;
    border: 1px solid $semanticPrimary;
    padding: 3px 16px;
    cursor: pointer;
  }

  &__transaction-button-text {
    font-size: $fontSizeSS;
    font-weight: 400;
    color: $semanticPrimary;
  }

  &__ellipsis-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__ellipsis-button {
    border-radius: 999px;
    &:hover,
    &--active {
      background-color: $semanticPrimarySubdued;
    }
  }

  &__ellipsis-icon {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    color: $textSecondary;
  }

  &__bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
  }

  &__dropdown {
    position: fixed;
    width: fit-content;
    height: min-content;
    background-color: white;
    z-index: 12;
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
    box-shadow: 0px 0px 22px 0px #80808066;
    overflow: scroll;
    border-radius: 4px;
    border: 1px solid $borderDefault;
  }

  &__dropdown-option {
    display: grid;
    grid-template-columns: min-content auto;
    gap: 4px;
    width: 100%;
    height: 36px;
    padding: 8px;
    align-items: center;
    &--active:hover {
      background-color: $semanticPrimarySubdued;
    }
  }

  &__dropdown-icon {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    color: $textSecondary;
  }

  &__dropdown-option-text {
    display: grid;
    align-items: center;
    justify-content: left;
    font-size: 14px;
    font-weight: 400;
    text-align: left;
  }
}
</style>
